"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactForm = () => {
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		subject: "",
		message: "",
	});
	const [formStatus, setFormStatus] = useState({ type: null, message: "" });

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		const templateParams = {
			from_name: formData.name,
			from_email: formData.email,
			subject: formData.subject,
			message: formData.message,
		};

		emailjs
			.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)
			.then(() => {
				setFormStatus({
					type: "success",
					message: "Thank you for your message. We'll get back to you soon!",
				});
				setFormData({ name: "", email: "", subject: "", message: "" });
			})
			.catch((error) => {
				setFormStatus({
					type: "error",
					message: "Oops! Something went wrong. Please try again later.",
				});
				console.error("EmailJS Error:", error);
			});
	};

	return (
		<motion.form
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.5 }}
			onSubmit={handleSubmit}
			className="space-y-6">
			<div>
				<label
					htmlFor="name"
					className="block text-sm font-medium text-gray-700">
					Name
				</label>
				<input
					type="text"
					id="name"
					name="name"
					value={formData.name}
					onChange={handleChange}
					required
					className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1666ba] focus:ring-[#1666ba] p-2 border"
				/>
			</div>
			<div>
				<label
					htmlFor="email"
					className="block text-sm font-medium text-gray-700">
					Email
				</label>
				<input
					type="email"
					id="email"
					name="email"
					value={formData.email}
					onChange={handleChange}
					required
					className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1666ba] focus:ring-[#1666ba] p-2 border"
				/>
			</div>
			<div>
				<label
					htmlFor="subject"
					className="block text-sm font-medium text-gray-700">
					Subject
				</label>
				<input
					type="text"
					id="subject"
					name="subject"
					value={formData.subject}
					onChange={handleChange}
					required
					className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1666ba] focus:ring-[#1666ba] p-2 border"
				/>
			</div>
			<div>
				<label
					htmlFor="message"
					className="block text-sm font-medium text-gray-700">
					Message
				</label>
				<textarea
					id="message"
					name="message"
					rows="4"
					value={formData.message}
					onChange={handleChange}
					required
					className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1666ba] focus:ring-[#1666ba] p-2 border"></textarea>
			</div>
			<div>
				<motion.button
					whileHover={{ scale: 1.05 }}
					whileTap={{ scale: 0.95 }}
					type="submit"
					className="w-full bg-[#1666ba] text-white px-6 py-3 rounded-md hover:bg-[#1254a1] transition-colors duration-200">
					Send Message
				</motion.button>
			</div>
			{formStatus.type === "success" && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					className="text-green-600 text-center p-4 bg-green-50 rounded-md">
					{formStatus.message}
				</motion.div>
			)}

			{formStatus.type === "error" && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					className="text-red-600 text-center p-4 bg-red-50 rounded-md">
					{formStatus.message}
				</motion.div>
			)}
		</motion.form>
	);
};

const Contact = () => {
	return (
		<div className="bg-white pt-20">
			{/* Hero Section */}
			<section className="relative py-20 bg-[#819f3d] text-white">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<motion.h1
						initial={{ y: -20, opacity: 0 }}
						animate={{ y: 0, opacity: 1 }}
						transition={{ duration: 0.8 }}
						className="text-4xl md:text-5xl font-bold text-center mb-6">
						Get in Touch
					</motion.h1>
				</div>
			</section>

			{/* Contact Information */}
			<section className="py-16">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
						<motion.div
							initial={{ opacity: 0, x: -50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8 }}>
							<h2 className="text-3xl font-bold text-[#1666ba] mb-8">
								Contact Information
							</h2>
							<div className="space-y-6">
								<div className="flex items-start">
									<svg
										className="w-6 h-6 mr-3 text-[#1666ba] mt-1 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
									</svg>
									<div>
										<h3 className="font-medium text-gray-800">Address</h3>
										<p className="text-gray-600">
											Office#118, B Block, 7 Evens Buildings, Dubai Investment
											Park-1, Dubai, UAE
										</p>
									</div>
								</div>

								<div className="flex items-start">
									<svg
										className="w-6 h-6 mr-3 text-[#1666ba] mt-1 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
									</svg>
									<div>
										<h3 className="font-medium text-gray-800">Email</h3>
										<p className="text-gray-600">official@sld.global</p>
									</div>
								</div>

								<div className="flex items-start">
									<svg
										className="w-6 h-6 mr-3 text-[#1666ba] mt-1 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
									</svg>
									<div>
										<h3 className="font-medium text-gray-800">Mobile</h3>
										<p className="text-gray-600">+1 (778) 793 6741</p>
									</div>
								</div>
							</div>

							{/* <div className="mt-10">
								<h3 className="text-xl font-semibold text-[#1666ba] mb-4">
									Connect With Us
								</h3>
								<div className="flex space-x-4">
									{["facebook", "twitter", "linkedin", "instagram"].map(
										(social) => (
											<motion.a
												key={social}
												href={`https://${social}.com`}
												target="_blank"
												rel="noopener noreferrer"
												whileHover={{ scale: 1.1 }}
												whileTap={{ scale: 0.9 }}
												className="bg-[#1666ba] text-white p-3 rounded-full hover:bg-[#1254a1] transition-colors duration-200">
												<span className="sr-only">{social}</span>
												<svg
													className="w-5 h-5"
													fill="currentColor"
													viewBox="0 0 24 24"
													aria-hidden="true">
													<path
														fillRule="evenodd"
														d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
														clipRule="evenodd"
													/>
												</svg>
											</motion.a>
										)
									)}
								</div>
							</div> */}
						</motion.div>

						<motion.div
							initial={{ opacity: 0, x: 50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8 }}
							className="bg-white p-8 rounded-lg shadow-lg">
							<h2 className="text-3xl font-bold text-[#1666ba] mb-6">
								Send Us a Message
							</h2>
							<ContactForm />
						</motion.div>
					</div>
				</div>
			</section>

			{/* Map Section */}
			{/* <section className="py-16 bg-gray-50">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-3xl font-bold text-center text-[#1666ba] mb-8">
						Visit Our Campus
					</motion.h2>
					<div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-lg">
						<iframe
							src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.2167339485736!2d-73.98823492404004!3d40.75889083560744!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25855c6480299%3A0x55194ec5a1ae072e!2sTimes%20Square!5e0!3m2!1sen!2sus!4v1687301562288!5m2!1sen!2sus"
							width="100%"
							height="450"
							style={{ border: 0 }}
							allowFullScreen=""
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
							title="Campus Map"></iframe>
					</div>
				</div>
			</section> */}
		</div>
	);
};

export default Contact;
